import React, { useEffect, useMemo, useRef } from "react";
import { Spinner, Stack } from "react-bootstrap";

import { GetWidgetsDataResponse } from "src/services/Firebase/data";
import { colors } from "src/styles";
import styled from "styled-components";
import * as d3 from "d3";

interface WidgetProps {
  caption: string;
  preloadedData?: GetWidgetsDataResponse;
}

interface VendorTypeData {
  typeName: string;
  typeColor: string;
  count: number;
}

const VENDOR_COLOR_MAP: Record<string, string> = {
  Internal: "#4472C4",
  External: "#ED7D31",
};

const WorkOrderStatuses = ({ caption, preloadedData }: WidgetProps) => {
  const data = preloadedData;
  const svgRef = useRef<SVGSVGElement>(null);

  const vendorData: VendorTypeData[] = useMemo(() => {
    if (!data?.widgetsData?.workRequestsVendorTypeStatistics) {
      return [];
    }
    return data.widgetsData.workRequestsVendorTypeStatistics.map((item) => ({
      typeName: item.category,
      typeColor: VENDOR_COLOR_MAP[item.category] ?? "#808080",
      count: item.count,
    }));
  }, [data]);

  const total = useMemo(() => {
    return vendorData.reduce((acc, item) => acc + item.count, 0);
  }, [vendorData]);

  useEffect(() => {
    if (!svgRef.current || vendorData.length === 0) return;

    const size = 100;
    const radius = size / 2;
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", size)
      .attr("height", size)
      .attr("viewBox", `${-radius} ${-radius} ${size} ${size}`)
      .attr("style", "display: block;");

    const pie = d3
      .pie<VendorTypeData>()
      .value((d) => d.count)
      .sort(null);

    const arc = d3
      .arc<d3.PieArcDatum<VendorTypeData>>()
      .innerRadius(radius * 0.62)
      .outerRadius(radius - 2);

    const arcs = svg.selectAll(".arc").data(pie(vendorData)).enter().append("g").attr("class", "arc");

    arcs
      .append("path")
      .attr("fill", (d) => d.data.typeColor)
      .attr("stroke", "white")
      .attr("stroke-width", 2)
      .style("opacity", 0.9)
      .transition()
      .duration(800)
      .ease(d3.easeCubicOut)
      .attrTween("d", (d) => {
        const start: d3.PieArcDatum<VendorTypeData> = { ...d, endAngle: d.startAngle };
        const interpolate = d3.interpolate(start, d);
        return (t) => arc(interpolate(t)) as string;
      });
  }, [vendorData]);

  return (
    <WidgetContainer>
      <Stack gap={3}>
        <HeaderRow>
          <CardTitle>{caption}</CardTitle>
        </HeaderRow>
        {data ? (
          vendorData.length > 0 ? (
            <ChartRow>
              <svg ref={svgRef} style={{ width: 100, height: 100, flexShrink: 0 }} />
              <Legend>
                {vendorData.map((item) => (
                  <LegendEntry key={item.typeName}>
                    <LegendTop>
                      <LegendDot style={{ background: item.typeColor }} />
                      <LegendLabel>{item.typeName}</LegendLabel>
                    </LegendTop>
                    <LegendValue>
                      {item.count} <LegendPct>{((item.count / total) * 100).toFixed(1)}%</LegendPct>
                    </LegendValue>
                  </LegendEntry>
                ))}
                <Divider>
                  Total: <strong>{total}</strong>
                </Divider>
              </Legend>
            </ChartRow>
          ) : (
            <EmptyState>No vendor data available.</EmptyState>
          )
        ) : (
          <Spinner animation="border" />
        )}
      </Stack>
    </WidgetContainer>
  );
};

export default WorkOrderStatuses;

const WidgetContainer = styled.div`
  display: flex;
  min-width: 0;
  background: #ffffff;
  border: 0.5px solid rgba(0, 0, 0, 0.12);
  border-radius: 12px;
  padding: 14px 16px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const CardTitle = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${colors.grey.dark};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const ChartRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const Legend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const LegendEntry = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const LegendTop = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
`;

const LegendDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 2px;
  flex-shrink: 0;
`;

const LegendLabel = styled.span`
  font-size: 11px;
  color: ${colors.grey.dark};
`;

const LegendValue = styled.div`
  font-size: 20px;
  font-weight: 500;
  color: ${colors.grey.dark3};
  padding-left: 16px;
`;

const LegendPct = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${colors.grey.light10};
`;

const Divider = styled.div`
  border-top: 0.5px solid ${colors.grey.light11};
  padding-top: 8px;
  font-size: 11px;
  color: ${colors.grey.dark};
`;

const EmptyState = styled.div`
  font-size: 13px;
  color: ${colors.grey.dark};
  padding: 16px 0;
`;
